/** @jsx jsx */
import React, { useRef } from 'react';
import { jsx } from '@emotion/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { Box, Checkbox, FormControlLabel } from '@material-ui/core';
import { Field, FieldProps, Form, FormikProps } from 'formik';

import styles from '../styles';

import {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
  Trans,
} from 'hooks/useTranslate/useTranslate';
import { CreditApplication } from 'state/creditApplication';

import PageContainer from 'components/PageContainer';
import Title from 'components/Title';
import ActionButtonContainer from 'components/ActionButtonsContainer';
import Button from 'components/Button';
import { TextFieldPopover } from 'components/TextField';
import OrderSummary from '../OrderSummary';
import ShippingAddress from '../ShippingAddress';
import ShippingMethod from '../ShippingMethod';
import PaymentPlan from '../PaymentPlan';
import DisclosureLink from 'features/unified/DisclosureLink/DisclosureLink';
import LoadingPage from 'components/LoadingPage';
import BackToPrevious from 'components/unified/BackToPrevious';
import { InstallmentOfferData } from 'state/financeOffers';
import { format } from 'date-fns/fp';

export interface FormValues {
  consent: boolean;
}

type PropTypes = FormikProps<FormValues> & {
  application: CreditApplication;
  installmentOfferData: InstallmentOfferData;
  paymentAgreementId: string;
  cachedLoanAgreementDisclosureContent: string;
  hasDisclosureError: boolean;
  handleDisclosureError: () => void;
  getDisclosureLoadingState: (type?: string) => boolean;
  setDisclosureLoadingState: (type: string, loading: boolean) => void;
};

const InstallmentPlaceOrderView: React.FC<PropTypes> = props => {
  const {
    isValid,
    isSubmitting,
    application,
    installmentOfferData,
    paymentAgreementId,
    cachedLoanAgreementDisclosureContent,
    hasDisclosureError,
    handleDisclosureError,
    getDisclosureLoadingState,
    setDisclosureLoadingState,
  } = props;
  const { order, shippingAddress } = application;
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const softInquiryRef = useRef<HTMLElement | null>(null);

  if (getDisclosureLoadingState('loan-agreement')) {
    return <LoadingPage headerTitle="Just a moment." />;
  }

  return (
    <Form noValidate>
      <PageContainer maxWidth="sm">
        <Box mb={2}>
          <Title css={styles.pageTitleText}>
            {tMd('unified.placeOrder.installments.title')}
          </Title>
        </Box>

        <Box mb={3} css={styles.subtitleText}>
          {tMd('unified.placeOrder.installments.subtitle', {
            firstPaymentDate: format(
              'MMM dd, yyyy',
              installmentOfferData.firstPaymentDate,
            ),
          })}
          <span css={styles.helpOutlineIcon}>
            <HelpOutlineIcon innerRef={softInquiryRef} />
          </span>
          <TextFieldPopover id="softInquiry" textFieldRef={softInquiryRef}>
            {tMdMultiline('unified.placeOrder.installments.popoverText')}
          </TextFieldPopover>
        </Box>

        {order ? (
          <Box mb={2} css={styles.bodyContainer}>
            <OrderSummary order={order} />
          </Box>
        ) : null}

        {shippingAddress ? (
          <Box mb={2} css={styles.bodyContainer}>
            <ShippingAddress shippingAddress={shippingAddress} />
          </Box>
        ) : null}

        {order !== null &&
        (order.shippingDescription ||
          order.totalShippingValue ||
          order.shippingProvider) ? (
          <Box mb={2} css={styles.bodyContainer}>
            <ShippingMethod order={order} />
          </Box>
        ) : null}

        <Box mb={2} css={styles.bodyContainer}>
          <PaymentPlan installmentOfferData={installmentOfferData} />
        </Box>

        <Box mb={3} css={styles.consentField}>
          <Field name="consent" type="checkbox">
            {(fieldProps: FieldProps) => (
              <FormControlLabel
                label={
                  <div css={styles.checkboxLabel}>
                    <Trans
                      i18nKey="unified.placeOrder.installments.consent"
                      components={[
                        <DisclosureLink
                          paymentAgreementId={paymentAgreementId}
                          type="tila"
                          onError={handleDisclosureError}
                          setLoadingState={setDisclosureLoadingState}
                        />,
                        <DisclosureLink
                          paymentAgreementId={paymentAgreementId}
                          type="credit-score-notice"
                          onError={handleDisclosureError}
                          setLoadingState={setDisclosureLoadingState}
                        />,
                        <DisclosureLink
                          paymentAgreementId={paymentAgreementId}
                          content={cachedLoanAgreementDisclosureContent}
                          onError={handleDisclosureError}
                          setLoadingState={setDisclosureLoadingState}
                        />,
                      ]}
                    />
                  </div>
                }
                control={
                  <Checkbox id="accountAssureConsent" {...fieldProps.field} />
                }
              />
            )}
          </Field>
        </Box>

        <ActionButtonContainer>
          <Button
            mobileFullWidth={true}
            type="submit"
            id="formSubmit"
            aria-label="formSubmit"
            disabled={
              !isValid ||
              isSubmitting ||
              hasDisclosureError ||
              getDisclosureLoadingState()
            }
            loading={isSubmitting}
          >
            {tMd('unified.placeOrder.label.submitButton')}
          </Button>

          <BackToPrevious tMdKey="unified.placeOrder.label.backButton" />
        </ActionButtonContainer>
      </PageContainer>
    </Form>
  );
};

export default InstallmentPlaceOrderView;
